import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '1200px',
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxHeight: '90vh',
    overflowY: 'auto'
  }
};

// Key Functional Attributes Dialog Component
const ManageRequirementExceptions = ({
  isOpen,
  onClose,
  selectedIds,
  corporateAccount,
  currentProject,
  login_token,
  canDeleteException,
  functionalLevelDescription,
  requirementIdWithPrefix,
  selectedLevelId,
  selectedRequirements
}) => {
  const [attributes, setAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [exceptionLevel, setExceptionLevel] = useState(() => {
    return selectedRequirements && selectedRequirements.size > 0 ? 'Requirement' : 'FunctionalLevel';
  });

  // Delete confirmation dialog state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Add ref for focus management
  const dialogRef = useRef(null);

  // Focus dialog when it opens
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    // Clear the array completely by setting to empty array
    setAttributes([]);
    // Clear selected attributes
    setSelectedAttributes(new Set());
    // Reset any error messages
    setError('');
    // Reset loading state
    setIsLoading(false);
  };

  // This effect controls the initialization and reset when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Ensure complete reset of data when dialog opens
      resetAllData();

      // Determine the correct exception level
      const newExceptionLevel = selectedRequirements && selectedRequirements.size > 0
        ? 'Requirement' : 'FunctionalLevel';

      // Set the exception level
      setExceptionLevel(newExceptionLevel);

      // Delay the fetch to ensure UI is completely updated
      setTimeout(() => {
        if (newExceptionLevel === 'Requirement' && selectedIds && selectedIds.length === 1) {
          fetchKeyAttributes();
        } else if (newExceptionLevel === 'FunctionalLevel' && selectedLevelId) {
          fetchKeyAttributes();
        }
      }, 100);
    }
  }, [isOpen]); // Only depend on isOpen to avoid multiple fetches

  // This effect handles changes in exception level
  useEffect(() => {
    if (isOpen) {
      // Always reset all data when exception level changes
      resetAllData();

      // Delay fetch to ensure UI is updated
      setTimeout(() => {
        fetchKeyAttributes();
      }, 100);
    }
  }, [exceptionLevel]);

  const fetchKeyAttributes = async () => {
    // Always start with a clean slate
    resetAllData();

    // Set loading state
    setIsLoading(true);

    // Check if we have valid selection
    if (exceptionLevel === 'FunctionalLevel' && !selectedLevelId) {
      setError('No functional level selected');
      setIsLoading(false);
      return;
    }

    if (exceptionLevel === 'Requirement' && (!selectedIds || selectedIds.length === 0)) {
      setError('No requirement selected');
      setIsLoading(false);
      return;
    }

    const requestBody = {
      corporate_account: corporateAccount,
      project_id: currentProject,
      level_id: exceptionLevel === 'FunctionalLevel' ? selectedLevelId : "",
      req_id: exceptionLevel === 'Requirement' && selectedIds && selectedIds.length === 1 ? selectedIds[0] : ""
    };

    console.log('Fetching with request:', JSON.stringify(requestBody));

    try {
      const response = await fetch(appConfig.getUrl('/get_key_attributes_list_requirements'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setAttributes(data.key_functional_attributes_list || []);
      } else {
        // Handle the case where the requirement level API fails
        if (exceptionLevel === 'Requirement') {
          setAttributes([]);
          setError('No matching exceptions found for the selected requirement');
        } else {
          throw new Error(data.status_description || 'Failed to fetch key attributes');
        }
      }
    } catch (err) {
      setError(err.message);
      if (exceptionLevel === 'Requirement') {
        setAttributes([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExceptionLevelChange = (newLevel) => {
    if (newLevel !== exceptionLevel) {
      // First reset all data
      resetAllData();

      // Then change the level (this will trigger the exceptionLevel useEffect)
      setExceptionLevel(newLevel);
    }
  };

  // Delete confirmation dialog handler
  const handleDeleteSelectedConfirm = () => {
    if (selectedAttributes.size === 0) {
      setError('No attributes selected for deletion');
      return;
    }
    setIsDeleteConfirmOpen(true);
  };

  // Actual delete function
  const handleDeleteSelected = async () => {
    setIsDeleteConfirmOpen(false);
    setIsLoading(true);

    try {
      const response = await fetch(appConfig.getUrl('/delete_key_attributes_list_requirements'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: exceptionLevel === 'FunctionalLevel' ? selectedLevelId : "",
          req_id: exceptionLevel === 'Requirement' && selectedIds && selectedIds.length === 1 ? selectedIds[0] : "",
          key_attribute_list_ids: Array.from(selectedAttributes)
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError('Exceptions deleted successfully');

        // Delay the refresh to show success message
        setTimeout(() => {
          // Complete reset of data
          resetAllData();

          // Then fetch updated list
          setTimeout(() => {
            fetchKeyAttributes();
          }, 100);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to delete key attributes');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px', padding: '0'}}>
          <div style={{
            backgroundColor: '#FFE6E6',
            color: '#CC0000',
            padding: '15px 20px',
            borderRadius: '8px 8px 0 0',
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div style={{ padding: '20px' }}>
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
              Are you sure you want to delete {selectedAttributes.size} selected exception{selectedAttributes.size !== 1 ? 's' : ''}?
            </p>
            <p style={{ fontSize: '12px', color: '#dc2626', marginBottom: '16px' }}>
              This action cannot be undone.
            </p>

            {/* Show some details about selected exceptions */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              maxHeight: '120px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Selected exceptions:</p>
              {Array.from(selectedAttributes).slice(0, 5).map(attrId => {
                const attr = attributes.find(a => a.key_attribute_list_id === attrId);
                return attr ? (
                  <div key={attrId} style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                    <span style={{
                      padding: '1px 6px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      backgroundColor: attr.include_exclude === 'INCLUDE' ? '#dcfce7' : '#fee2e2',
                      color: attr.include_exclude === 'INCLUDE' ? '#166534' : '#991b1b',
                      marginRight: '6px'
                    }}>
                      {attr.include_exclude === 'INCLUDE' ? 'INCLUDE' : 'EXCLUDE'}
                    </span>
                    {attr.attribute_name} - {attr.attribute_category}
                  </div>
                ) : null;
              })}
              {selectedAttributes.size > 5 && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  ... and {selectedAttributes.size - 5} more
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '0 20px 20px 20px'
          }}>
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Delete {selectedAttributes.size} Exception{selectedAttributes.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{
          ...DialogStyles.dialog,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Manage Exceptions
        </h2>

        <div style={{
          marginBottom: '24px',
          padding: '16px',
          border: '1px solid #d1d5db',
          borderRadius: '6px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px'
          }}>
            Please select the level from the below options:
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: !selectedLevelId ? 0.6 : 1
            }}>
              <input
                type="radio"
                name="exceptionLevel"
                style={{ width: '20px', height: '20px' }}
                checked={exceptionLevel === 'FunctionalLevel'}
                onChange={() => handleExceptionLevelChange('FunctionalLevel')}
                disabled={!selectedLevelId}
              />
              <span>Functional Level : {selectedLevelId ? functionalLevelDescription : 'No level selected'}</span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: selectedRequirements.size === 0 ? 0.6 : 1
            }}>
              <input
                type="radio"
                name="exceptionLevel"
                style={{ width: '20px', height: '20px' }}
                checked={exceptionLevel === 'Requirement'}
                onChange={() => handleExceptionLevelChange('Requirement')}
                disabled={selectedRequirements.size === 0}
              />
              <span>
                {selectedRequirements.size === 0 ? (
                  'Requirement : No requirement selected'
                ) : selectedRequirements.size === 1 ? (
                  `Requirement : ${requirementIdWithPrefix}`
                ) : (
                  `Requirements : ${selectedRequirements.size} requirements selected`
                )}
              </span>
            </label>
          </div>
        </div>

        <h3 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '16px'
        }}>
          {exceptionLevel === 'Requirement'
            ? `Exceptions for Requirement - ${requirementIdWithPrefix || 'N/A'}`
            : `Exceptions for Functional Level: ${functionalLevelDescription || 'N/A'}`}
        </h3>

        {error && (
          <div style={{
            marginBottom: '15px',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
            color: error.includes('successfully') ? '#059669' : '#dc2626',
            border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
          }}>
            {error}
          </div>
        )}

        {attributes.length === 0 && !isLoading && !error && (
          <div style={{
            marginBottom: '15px',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: '#fffbeb',
            color: '#d97706',
            border: '1px solid #f59e0b'
          }}>
            No matching entries found
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', marginBottom: '20px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <tr>
                <th style={{ padding: '8px', textAlign: 'left' }}>
                  <input
                    type="checkbox"
                    checked={attributes.length > 0 && selectedAttributes.size === attributes.length}
                    onChange={(e) => {
                      const newSelected = new Set();
                      if (e.target.checked) {
                        attributes.forEach(attr => newSelected.add(attr.key_attribute_list_id));
                      }
                      setSelectedAttributes(newSelected);
                    }}
                  />
                </th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Exception Type</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Attribute Name</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                    Loading...
                  </td>
                </tr>
              ) : attributes.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                    No key functional attributes found.
                  </td>
                </tr>
              ) : (
                attributes.map(attr => (
                  <tr
                    key={attr.key_attribute_list_id}
                    style={{
                      borderTop: '1px solid #e5e7eb'
                    }}
                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '8px' }}>
                      <input
                        type="checkbox"
                        checked={selectedAttributes.has(attr.key_attribute_list_id)}
                        onChange={() => {
                          const newSelected = new Set(selectedAttributes);
                          if (newSelected.has(attr.key_attribute_list_id)) {
                            newSelected.delete(attr.key_attribute_list_id);
                          } else {
                            newSelected.add(attr.key_attribute_list_id);
                          }
                          setSelectedAttributes(newSelected);
                        }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        backgroundColor: attr.include_exclude === 'INCLUDE' ? '#dcfce7' : '#fee2e2',
                        color: attr.include_exclude === 'INCLUDE' ? '#166534' : '#991b1b'
                      }}>
                        {attr.include_exclude === 'INCLUDE' ? 'Only applies to' : 'Does not apply to'}
                      </span>
                    </td>
                    <td style={{ padding: '8px' }}>{attr.attribute_name}</td>
                    <td style={{ padding: '8px' }}>{attr.attribute_category}</td>
                    <td style={{ padding: '8px' }}>{attr.attribute_description}</td>
                    <td style={{ padding: '8px' }}>{new Date(attr.created_date).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <div
            style={{ position: 'relative' }}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector('.delete-tooltip');
              if (tooltip) tooltip.style.visibility = 'visible';
            }}
            onMouseLeave={(e) => {
              const tooltip = e.currentTarget.querySelector('.delete-tooltip');
              if (tooltip) tooltip.style.visibility = 'hidden';
            }}
          >
            <button
              style={{
                padding: '8px 16px',
                color: 'white',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: (!canDeleteException || selectedAttributes.size === 0) ? '#fca5a5' : '#ef4444',
                fontSize: '14px',
                opacity: (!canDeleteException || selectedAttributes.size === 0) ? 0.6 : 1
              }}
              disabled={selectedAttributes.size === 0 || !canDeleteException}
              onClick={handleDeleteSelectedConfirm}
            >
              Delete Selected
            </button>

            {/* Delete button tooltip */}
            {(!canDeleteException || selectedAttributes.size === 0) && (
              <div
                className="delete-tooltip"
                style={{
                  position: 'absolute',
                  left: '0',
                  bottom: '100%',
                  marginBottom: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#374151',
                  color: 'white',
                  fontSize: '12px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  visibility: 'hidden',
                  whiteSpace: 'nowrap',
                  zIndex: 20
                }}
              >
                {selectedAttributes.size === 0
                  ? "Select one or more exceptions to delete"
                  : "Insufficient permission to delete exceptions"
                }
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '16px',
                  width: '0',
                  height: '0',
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #374151'
                }}></div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {selectedAttributes.size > 0 ? `${selectedAttributes.size} selected` : ''}
            </span>
            <button
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {renderDeleteConfirmDialog()}
      </div>
    </div>
  );
};

export default ManageRequirementExceptions;